'use client';

import type { Sort } from '@ria-client/advisor-search/constants';
import {
  buildResultsHref,
  parseFindAdvisorSearchParams,
  searchParamsToObject,
} from '@ria-client/advisor-search/query';
import type { SearchParams, SearchResponse } from '@ria-client/advisor-search/types';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useTransition } from 'react';

import type { FilterValues } from '../FiltersDialog/FiltersDialog';

export interface ResultsStateHandlers {
  onPageChange: (page: number) => void;
  onApply: (values: FilterValues) => void;
  onSortChange: (sort: Sort) => void;
  onRetry: () => void;
}

export interface ResultsState {
  params: SearchParams;
  data: SearchResponse | null;
  error: string | null;
  isPending: boolean;
  handlers: ResultsStateHandlers;
}

export interface UseResultsStateInput {
  initialParams: SearchParams;
  initialData: SearchResponse | null;
  initialError: string | null;
}

export function useResultsState({
  initialParams,
  initialData,
  initialError,
}: UseResultsStateInput): ResultsState {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const params = useMemo(() => {
    const parsed = parseFindAdvisorSearchParams(searchParamsToObject(searchParams));
    return parsed.kind === 'redirect' ? initialParams : parsed.params;
  }, [searchParams, initialParams]);

  const navigate = useCallback(
    (href: string, scrollToTop = false) => {
      startTransition(() => {
        router.push(href);
        if (scrollToTop && typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    },
    [router],
  );

  const onPageChange = useCallback(
    (page: number) => {
      if (page === (params.page ?? 1)) return;
      navigate(buildResultsHref({ ...params, page }), true);
    },
    [params, navigate],
  );

  const onApply = useCallback(
    (values: FilterValues) => {
      navigate(
        buildResultsHref({
          ...params,
          services: [...values.services],
          fees: [...values.fees],
          clientTypes: [...values.clientTypes],
          experience: [...values.experience],
          page: 1,
        }),
      );
    },
    [params, navigate],
  );

  const onSortChange = useCallback(
    (sort: Sort) => {
      navigate(buildResultsHref({ ...params, sort, page: 1 }));
    },
    [params, navigate],
  );

  const onRetry = useCallback(() => {
    startTransition(() => {
      router.refresh();
    });
  }, [router]);

  return {
    params,
    data: initialData,
    error: initialError,
    isPending,
    handlers: { onPageChange, onApply, onSortChange, onRetry },
  };
}
